import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import BackgroundVideoStrip from "@/components/BackgroundVideoStrip";
import { BG_VIDEOS } from "@/data/backgroundVideos";
import {
  Search,
  Briefcase,
  Tag,
  Calendar,
  Home,
  Car,
  Newspaper,
  Plus,
  Send,
  Loader2,
  X,
  Bot,
  MapPin,
  Clock,
  DollarSign,
  ExternalLink,
  Sparkles,
  ArrowRight,
  Phone,
  User,
  Filter,
  ChevronDown,
  Grid3X3,
  List,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { MarketplaceFilters, ListingCard, CompareBar, MarketplaceSearchHeader } from "@/components/marketplace";
import SaveSearchButton from "@/components/marketplace/SaveSearchButton";
import { VoiceSearchButton } from "@/components/VoiceSearchButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default AI Voice Agent number (fallback for call button)
const DEFAULT_AI_VOICE_PHONE = "833-772-7397";

// Listing type configurations - Order: Vehicles, Real Estate, Furniture, RVs, Jobs, Events, Deals
const listingTypes = [
  {
    id: "vehicles",
    name: "Vehicles",
    icon: Car,
    color: "#EF4444",
    description: "Cars, trucks & more",
    postButton: "+ List Vehicle",
    heroName: "TONY",
    heroGreeting: "Hey! I'm TONY, your AI Vehicles Hero! Ready to sell your vehicle and find the perfect buyer?",
    supportsUploadList: true,
    uploadListText: "Are you a dealer? Upload your entire vehicle inventory at once!",
    hasAdvancedFilters: true,
  },
  {
    id: "real_estate",
    name: "Real Estate",
    mobileShortName: "Homes",
    icon: Home,
    color: "#EC4899",
    description: "Properties for sale & rent",
    postButton: "+ List Property",
    heroName: "MARCUS",
    heroGreeting: "Hi! I'm MARCUS, your AI Real Estate Hero! Have a property to list? I can help you reach thousands of qualified buyers!",
    supportsUploadList: true,
    uploadListText: "Are you a realtor or property manager? Upload your entire property inventory at once!",
    hasAdvancedFilters: true,
  },
  {
    id: "furniture",
    name: "Furniture",
    icon: Home,
    color: "#8B5CF6",
    description: "Furniture & home goods",
    postButton: "+ List Furniture",
    heroName: "OLIVIA",
    heroGreeting: "Hi! I'm OLIVIA, your AI Furniture Hero! Have furniture to sell? Let's get it in front of thousands of buyers!",
    supportsUploadList: true,
    uploadListText: "Are you a furniture dealer or store? Upload your entire furniture inventory at once!",
    hasAdvancedFilters: true,
  },
  {
    id: "rvs",
    name: "RVs",
    icon: Car,
    color: "#06B6D4",
    description: "RVs, campers & trailers",
    postButton: "+ List RV",
    heroName: "RILEY",
    heroGreeting: "Hey there! I'm RILEY, your AI RV Hero! Ready to sell your RV, camper, or trailer? Let's find you the perfect buyer!",
    supportsUploadList: true,
    uploadListText: "Are you an RV dealer? Upload your entire RV inventory at once!",
    hasAdvancedFilters: true,
  },
  {
    id: "jobs",
    name: "Jobs",
    icon: Briefcase,
    color: "#00E5FF",
    description: "Find your next opportunity",
    postButton: "+ Post Job",
    heroName: "JORDAN",
    heroGreeting: "Hi! I'm JORDAN, your AI Job Posting Hero! Looking to post a job and find the perfect candidates?",
  },
  {
    id: "events",
    name: "Events",
    icon: Calendar,
    color: "#F59E0B",
    description: "Upcoming events near you",
    postButton: "+ Post Event",
    heroName: "ELENA",
    heroGreeting: "Hello! I'm ELENA, your AI Events Hero! Got an event you want to promote and fill with excited attendees?",
  },
  {
    id: "deals",
    name: "Deals",
    icon: Tag,
    color: "#10B981",
    description: "Exclusive deals & coupons",
    postButton: "+ List Deal",
    heroName: "MAYA",
    heroGreeting: "Hey there! I'm MAYA, your AI Deals Hero! Have a special offer or coupon to share with the world?",
  },
];

// AI Hero avatars/images for each listing type
const heroImages = {
  jobs: "https://customer-assets.emergentagent.com/job_1311d09a-531f-4860-b5ab-e067dc11b06d/artifacts/ydox3cgb_ChatGPT%20Image%20Feb%203%2C%202026%2C%2001_17_51%20PM.png",
  deals: "https://customer-assets.emergentagent.com/job_1311d09a-531f-4860-b5ab-e067dc11b06d/artifacts/rjb518jb_ChatGPT%20Image%20Feb%203%2C%202026%2C%2001_23_26%20PM.png",
  events: "https://customer-assets.emergentagent.com/job_1311d09a-531f-4860-b5ab-e067dc11b06d/artifacts/2kvq0aw3_ChatGPT%20Image%20Feb%203%2C%202026%2C%2001_31_44%20PM.png",
  real_estate: "https://customer-assets.emergentagent.com/job_1311d09a-531f-4860-b5ab-e067dc11b06d/artifacts/xs0rcc5u_ChatGPT%20Image%20Feb%203%2C%202026%2C%2001_28_31%20PM.png",
  vehicles: "https://customer-assets.emergentagent.com/job_1311d09a-531f-4860-b5ab-e067dc11b06d/artifacts/6l02z6bb_ChatGPT%20Image%20Feb%203%2C%202026%2C%2001_35_53%20PM.png",
  furniture: "https://customer-assets.emergentagent.com/job_b1b5869b-65cc-4b1f-ac0a-6c8ed691ccb8/artifacts/aau7dz9c_ChatGPT%20Image%20Feb%205%2C%202026%2C%2001_38_25%20PM.png",
  rvs: "https://customer-assets.emergentagent.com/job_b1b5869b-65cc-4b1f-ac0a-6c8ed691ccb8/artifacts/saw7oteq_ChatGPT%20Image%20Feb%205%2C%202026%2C%2001_40_33%20PM.png",
};

// Sort options
const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "price_low", label: "Price: Low to High" },
  { value: "price_high", label: "Price: High to Low" },
  { value: "mileage", label: "Lowest Mileage" },
];

// Posting Chat Modal (preserved from original)
function PostingChatModal({ listingType, isOpen, onClose }) {
  const config = listingTypes.find(t => t.id === listingType) || listingTypes[0];
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `post-${listingType}-${Date.now()}`);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [capturePhase, setCapturePhase] = useState("chat");
  const [leadData, setLeadData] = useState({ name: "", email: "", phone: "" });
  const [isIndividual, setIsIndividual] = useState(false);
  const [isUploadList, setIsUploadList] = useState(false);
  const messagesEndRef = useRef(null);

  const supportsIndividual = ["furniture", "vehicles", "real_estate", "rvs"].includes(listingType);
  const supportsUploadList = config.supportsUploadList;

  useEffect(() => {
    if (isOpen && config.heroGreeting) {
      let greeting = config.heroGreeting + "\n\nI can help you get your listing live AND show you how to boost it with Cinematic Ads across Facebook, Instagram, TikTok, Google, TV, Radio, and more!\n\nPlus, have you heard about our $120K Business Co-Op Funding? Let me tell you about it!";
      
      if (supportsUploadList) {
        greeting += `\n\n📦 **${config.uploadListText}** Click "Upload a List" below to get started!`;
      }
      
      setMessages([{ role: "assistant", content: greeting }]);
      setCapturePhase("chat");
      setLeadCaptured(false);
      setLeadData({ name: "", email: "", phone: "" });
      setIsIndividual(false);
      setIsUploadList(false);
    }
  }, [isOpen, config.heroGreeting, config.uploadListText, supportsUploadList]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleIndividualSeller = () => {
    setIsIndividual(true);
    setMessages((prev) => [
      ...prev,
      { role: "user", content: "I am an individual seller, not a business or agent" },
      { 
        role: "assistant", 
        content: `No problem at all! We love individual sellers! 🎉\n\nHere's the great news - for just $9, you can:\n\n✓ Post your ${listingType.replace("_", " ")} listing and reach thousands of buyers\n✓ Get your own profile page on the Best of Best Directory\n✓ Become an affiliate and earn commissions by referring others!\n\nPlus, your listing can be boosted with professional Cinematic Ads on Facebook, Instagram, and more!\n\nReady to get started? What's your full name?`
      }
    ]);
    setCapturePhase("name");
  };

  const handleUploadList = () => {
    setIsUploadList(true);
    const typeLabel = listingType === "real_estate" ? "properties" : 
                      listingType === "vehicles" ? "vehicles" : 
                      listingType === "furniture" ? "furniture items" : 
                      listingType === "rvs" ? "RVs" : "items";
    setMessages((prev) => [
      ...prev,
      { role: "user", content: "I want to upload my entire inventory list" },
      { 
        role: "assistant", 
        content: `Excellent choice! 🚀 Uploading your full ${typeLabel} inventory is the BEST way to maximize your exposure!\n\n**Here's what you get with bulk listing upload:**\n\n✓ All your ${typeLabel} listed on our directory\n✓ Each listing gets its own AI-powered page\n✓ Retargeting pixels fire on EVERY listing view\n✓ Cinematic video ads created for your top ${typeLabel}\n✓ Promotion across Facebook, Instagram, TikTok, Google, TV & Radio\n\n**To upload your inventory list:**\n\n1. Log into your POD Dashboard\n2. Go to "Inventory Upload" section\n3. Upload your CSV/spreadsheet with all your ${typeLabel}\n4. Our system syncs them automatically to the directory!\n\nWant me to help you get set up with POD access? What's your full name?`
      }
    ]);
    setCapturePhase("name");
  };

  const sendMessage = async (overrideMessage = null) => {
    const messageToSend = overrideMessage || inputValue.trim();
    if (!messageToSend || isLoading) return;

    setInputValue("");
    setMessages((prev) => [...prev, { role: "user", content: messageToSend }]);
    setIsLoading(true);

    if (capturePhase === "name") {
      setLeadData(prev => ({ ...prev, name: messageToSend }));
      setMessages((prev) => [...prev, { role: "assistant", content: `Great to meet you, ${messageToSend}! What's the best email to reach you at?` }]);
      setCapturePhase("email");
      setIsLoading(false);
      return;
    }

    if (capturePhase === "email") {
      setLeadData(prev => ({ ...prev, email: messageToSend }));
      setMessages((prev) => [...prev, { role: "assistant", content: "Perfect! And what's the best phone number to reach you? (Or type 'skip' to continue)" }]);
      setCapturePhase("phone");
      setIsLoading(false);
      return;
    }

    if (capturePhase === "phone") {
      const phone = messageToSend.toLowerCase() === "skip" ? "" : messageToSend;
      const finalLead = { ...leadData, phone };
      
      try {
        await axios.post(`${API}/leads`, {
          business_id: "admin-directory",
          first_name: finalLead.name,
          email: finalLead.email,
          phone: finalLead.phone || null,
          message: JSON.stringify(messages),
          lead_type: `${listingType} Posting Lead`,
          source: "marketplace"
        });
      } catch (err) {
        console.error("Lead capture error:", err);
      }
      
      setLeadCaptured(true);
      setMessages((prev) => [...prev, { 
        role: "assistant", 
        content: `Awesome, ${finalLead.name}! 🎉\n\nYou're all set! Here's what happens next:\n\n1. Head to https://localbest.ai to log in or create your FREE account\n\n2. For just $9, you can post your listing AND get full access to our POD (Publishing On Demand) system!\n\n3. Don't forget to get pre-qualified for the $120K Business Co-Op Funding!\n\nYour listing can be boosted with Cinematic Ads across Facebook, Instagram, TikTok, Google, TV, Radio, Postcards, and Retargeting!\n\nClick the button below to get started! 🚀` 
      }]);
      setCapturePhase("complete");
      setIsLoading(false);
      return;
    }

    const readyKeywords = ["post", "list", "start", "ready", "sign up", "create", "yes", "let's go", "interested"];
    const wantsToPost = readyKeywords.some(kw => messageToSend.toLowerCase().includes(kw));

    if (wantsToPost && !leadCaptured) {
      setMessages((prev) => [...prev, { 
        role: "assistant", 
        content: "Fantastic! I'm so excited to help you get started! 🎉\n\nLet me get a few quick details so we can set up your account.\n\nWhat's your full name?" 
      }]);
      setCapturePhase("name");
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/chat/listing`, {
        message: messageToSend,
        listing_type: listingType,
        session_id: sessionId,
      });
      setMessages((prev) => [...prev, { role: "assistant", content: response.data.response }]);
    } catch (error) {
      console.error("Chat error:", error);
      setMessages((prev) => [...prev, { 
        role: "assistant", 
        content: "I'm having a small connection issue. But I'm still here to help! Would you like to get started with your listing? Just say 'yes' and I'll guide you through!" 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-lg h-[650px] glass-card rounded-2xl flex flex-col overflow-hidden border-2"
          style={{ borderColor: `${config.color}50`, boxShadow: `0 0 60px ${config.color}30` }}
        >
          {/* Header */}
          <div 
            className="p-4 flex items-center gap-4 border-b border-white/10"
            style={{ background: `linear-gradient(135deg, ${config.color}20, transparent)` }}
          >
            <img
              src={heroImages[listingType] || heroImages.jobs}
              alt={config.heroName}
              className="w-14 h-14 rounded-full border-2 object-cover"
              style={{ borderColor: config.color }}
            />
            <div className="flex-1">
              <h3 className="text-white font-bold font-['Outfit'] text-lg">{config.heroName}</h3>
              <p className="text-sm" style={{ color: config.color }}>AI {config.name} Hero™</p>
            </div>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors">
              <X className="w-5 h-5 text-[#94A3B8]" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] p-3 rounded-2xl text-sm whitespace-pre-wrap ${
                    msg.role === "user"
                      ? "text-white rounded-br-sm"
                      : "bg-white/10 text-[#E2E8F0] rounded-bl-sm"
                  }`}
                  style={msg.role === "user" ? { backgroundColor: `${config.color}40` } : {}}
                >
                  {msg.content}
                </div>
              </motion.div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white/10 p-3 rounded-2xl rounded-bl-sm flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" style={{ color: config.color }} />
                  <span className="text-[#94A3B8] text-sm">{config.heroName} is typing...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* CTA Button when lead is captured */}
          {capturePhase === "complete" && (
            <div className="px-4 py-3 border-t border-white/5 bg-gradient-to-r from-[#10B981]/20 to-[#00E5FF]/20">
              <a href="https://localbest.ai" target="_blank" rel="noopener noreferrer" className="block">
                <Button className="w-full h-12 text-lg font-bold" style={{ backgroundColor: config.color }}>
                  Get Started at localbest.ai <ExternalLink className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>
          )}

          {/* Quick Actions */}
          {capturePhase === "chat" && messages.length < 3 && (
            <div className="px-4 py-3 border-t border-white/5 flex flex-wrap gap-2">
              <button
                onClick={() => sendMessage("I want to post a listing")}
                className="px-4 py-2 rounded-full text-xs font-medium transition-colors border"
                style={{ backgroundColor: `${config.color}20`, color: config.color, borderColor: `${config.color}50` }}
              >
                {config.postButton || "+ Post Listing"}
              </button>
              <button
                onClick={() => sendMessage("Tell me about the $120K Co-Op funding")}
                className="px-4 py-2 rounded-full bg-[#F59E0B]/20 text-[#F59E0B] text-xs font-medium hover:bg-[#F59E0B]/30 transition-colors border border-[#F59E0B]/50"
              >
                $120K Funding Info
              </button>
            </div>
          )}

          {/* Individual Seller Option */}
          {supportsIndividual && capturePhase === "chat" && !isIndividual && !isUploadList && (
            <div className="px-4 py-2 border-t border-white/5 bg-white/5">
              <button
                onClick={handleIndividualSeller}
                className="w-full py-2 rounded-lg bg-[#64748B]/20 text-[#94A3B8] text-sm hover:bg-[#64748B]/30 hover:text-white transition-colors border border-[#64748B]/30"
              >
                👤 I am an individual seller (not a business or agent)
              </button>
            </div>
          )}

          {/* Upload a List Option */}
          {supportsUploadList && capturePhase === "chat" && !isIndividual && !isUploadList && (
            <div className="px-4 py-2 border-t border-white/5 bg-white/5">
              <button
                onClick={handleUploadList}
                className="w-full py-2 rounded-lg bg-[#F59E0B]/20 text-[#F59E0B] text-sm hover:bg-[#F59E0B]/30 hover:text-white transition-colors border border-[#F59E0B]/30 font-medium"
              >
                📦 Upload a List (Dealers, Realtors, Stores)
              </button>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-white/10 bg-white/5">
            <div className="flex items-center gap-2">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                placeholder={
                  capturePhase === "name" ? "Enter your full name..." :
                  capturePhase === "email" ? "Enter your email..." :
                  capturePhase === "phone" ? "Enter your phone or 'skip'..." :
                  `Chat with ${config.heroName}...`
                }
                className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
                disabled={isLoading || capturePhase === "complete"}
              />
              <Button
                onClick={() => sendMessage()}
                disabled={isLoading || !inputValue.trim() || capturePhase === "complete"}
                className="px-4"
                style={{ backgroundColor: config.color }}
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function MarketplacePage() {
  const { type: urlType } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [activeType, setActiveType] = useState(urlType || "vehicles");
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [listings, setListings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [postingModalOpen, setPostingModalOpen] = useState(false);
  const [postingType, setPostingType] = useState("jobs");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  
  // Facets from API
  const [facets, setFacets] = useState({});
  
  // Compare feature
  const [compareItems, setCompareItems] = useState([]);
  
  // Sort
  const [sortBy, setSortBy] = useState("newest");
  
  // Advanced filters state
  const [filters, setFilters] = useState({
    state: searchParams.get("state") || "",
    city: searchParams.get("city") || "",
    zip_code: searchParams.get("zip") || "",
    radius: searchParams.get("radius") ? Number(searchParams.get("radius")) : null,
    min_price: searchParams.get("minPrice") ? Number(searchParams.get("minPrice")) : null,
    max_price: searchParams.get("maxPrice") ? Number(searchParams.get("maxPrice")) : null,
    condition: searchParams.get("condition") || "",
    // Vehicle
    make: searchParams.get("make") || "",
    model: searchParams.get("model") || "",
    min_year: searchParams.get("minYear") ? Number(searchParams.get("minYear")) : null,
    max_year: searchParams.get("maxYear") ? Number(searchParams.get("maxYear")) : null,
    min_mileage: searchParams.get("minMileage") ? Number(searchParams.get("minMileage")) : null,
    max_mileage: searchParams.get("maxMileage") ? Number(searchParams.get("maxMileage")) : null,
    body_style: searchParams.get("bodyStyle") || "",
    transmission: searchParams.get("transmission") || "",
    drivetrain: searchParams.get("drivetrain") || "",
    fuel_type: searchParams.get("fuelType") || "",
    // Real Estate
    property_type: searchParams.get("propertyType") || "",
    min_beds: searchParams.get("minBeds") ? Number(searchParams.get("minBeds")) : null,
    min_baths: searchParams.get("minBaths") ? Number(searchParams.get("minBaths")) : null,
    min_sqft: searchParams.get("minSqft") ? Number(searchParams.get("minSqft")) : null,
    max_sqft: searchParams.get("maxSqft") ? Number(searchParams.get("maxSqft")) : null,
    status: searchParams.get("status") || "",
    // RV
    rv_type: searchParams.get("rvType") || "",
    min_sleeps: searchParams.get("minSleeps") ? Number(searchParams.get("minSleeps")) : null,
    min_length: searchParams.get("minLength") ? Number(searchParams.get("minLength")) : null,
    max_length: searchParams.get("maxLength") ? Number(searchParams.get("maxLength")) : null,
    min_slideouts: searchParams.get("minSlideouts") ? Number(searchParams.get("minSlideouts")) : null,
    // Furniture
    category: searchParams.get("category") || "",
    style: searchParams.get("style") || "",
    material: searchParams.get("material") || "",
    room_type: searchParams.get("roomType") || "",
    // General
    has_photos: searchParams.get("hasPhotos") === "true" || null,
    has_video: searchParams.get("hasVideo") === "true" || null,
    featured: searchParams.get("featured") === "true" || null,
  });

  // Check for mobile
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update active type when URL changes
  useEffect(() => {
    if (urlType) {
      setActiveType(urlType);
      setPage(1);
    }
  }, [urlType]);

  // Fetch listings with advanced filters
  const fetchListings = useCallback(async () => {
    setIsLoading(true);
    
    const activeConfig = listingTypes.find(t => t.id === activeType);
    
    // Use advanced search for all inventory types including "all"
    if (activeType === "all" || activeConfig?.hasAdvancedFilters) {
      try {
        const params = new URLSearchParams();
        if (activeType !== "all") {
          params.append("listing_type", activeType);
        }
        params.append("page", page.toString());
        params.append("limit", "24");
        params.append("sort_by", sortBy);
        
        if (searchQuery) params.append("q", searchQuery);
        
        // Add all non-empty filters
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== null && value !== "" && value !== false) {
            params.append(key, value.toString());
          }
        });
        
        const response = await axios.get(`${API}/marketplace/search?${params.toString()}`);
        setListings(response.data.listings || []);
        setTotalPages(response.data.total_pages || 1);
        setTotalResults(response.data.total || 0);
        setFacets(response.data.facets || {});
      } catch (error) {
        console.error("Error fetching marketplace listings:", error);
        setListings([]);
      }
    } else {
      // Use old content API for jobs, deals, events
      try {
        const contentTypes = {
          all: null,
          jobs: "job_listing",
          deals: "coupon",
          events: "event",
        };
        
        const params = new URLSearchParams();
        if (contentTypes[activeType]) {
          params.append("content_type", contentTypes[activeType]);
        }
        if (searchQuery) {
          params.append("search", searchQuery);
        }
        if (filters.city) {
          params.append("city", filters.city);
        }
        if (filters.state) {
          params.append("state", filters.state);
        }
        
        const response = await axios.get(`${API}/content?${params.toString()}`);
        setListings(response.data || []);
        setTotalResults(response.data?.length || 0);
      } catch (error) {
        console.error("Error fetching content:", error);
        setListings([]);
      }
    }
    
    setIsLoading(false);
  }, [activeType, searchQuery, filters, page, sortBy]);

  useEffect(() => {
    const debounce = setTimeout(fetchListings, 300);
    return () => clearTimeout(debounce);
  }, [fetchListings]);

  // Update URL params when filters change
  const updateURLParams = useCallback(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set("q", searchQuery);
    if (filters.state) params.set("state", filters.state);
    if (filters.city) params.set("city", filters.city);
    if (filters.min_price) params.set("minPrice", filters.min_price.toString());
    if (filters.max_price) params.set("maxPrice", filters.max_price.toString());
    if (filters.make) params.set("make", filters.make);
    if (filters.model) params.set("model", filters.model);
    if (filters.condition) params.set("condition", filters.condition);
    
    setSearchParams(params, { replace: true });
  }, [searchQuery, filters, setSearchParams]);

  const handleApplyFilters = () => {
    setPage(1);
    updateURLParams();
    fetchListings();
  };

  const handleClearFilters = () => {
    setFilters({
      state: "", city: "", zip_code: "", radius: null,
      min_price: null, max_price: null, condition: "",
      make: "", model: "", min_year: null, max_year: null,
      min_mileage: null, max_mileage: null, body_style: "",
      transmission: "", drivetrain: "", fuel_type: "",
      property_type: "", min_beds: null, min_baths: null,
      min_sqft: null, max_sqft: null, status: "",
      rv_type: "", min_sleeps: null, min_length: null,
      max_length: null, min_slideouts: null,
      category: "", style: "", material: "", room_type: "",
      has_photos: null, has_video: null, featured: null,
    });
    setSearchQuery("");
    setPage(1);
    setSearchParams({}, { replace: true });
  };

  const countActiveFilters = () => {
    let count = 0;
    Object.values(filters).forEach(value => {
      if (value !== null && value !== "" && value !== false) count++;
    });
    if (searchQuery) count++;
    return count;
  };

  // Compare handlers
  const handleCompare = (listing) => {
    if (compareItems.find(i => i.id === listing.id)) {
      setCompareItems(compareItems.filter(i => i.id !== listing.id));
    } else if (compareItems.length < 4) {
      setCompareItems([...compareItems, listing]);
    }
  };

  const openPostingModal = (type) => {
    setPostingType(type);
    setPostingModalOpen(true);
  };

  const activeConfig = listingTypes.find(t => t.id === activeType);
  const hasAdvancedFilters = activeConfig?.hasAdvancedFilters;

  return (
    <div className="page-container min-h-screen" data-testid="marketplace-page">
      {/* Hero Section - Compact */}
      <section className="py-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#7C3AED]/10 via-transparent to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-6"
          >
            <h1 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-2">
              LocalBest.ai <span className="neon-text">Marketplace</span>
            </h1>
            <p className="text-[#94A3B8] text-base max-w-xl mx-auto">
              Find vehicles, real estate, RVs, furniture, jobs, deals & more
            </p>
          </motion.div>

          {/* Listing Type Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            {listingTypes.map((type) => (
              <Link
                key={type.id}
                to={`/marketplace/${type.id}`}
                onClick={() => { setActiveType(type.id); setPage(1); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  activeType === type.id
                    ? "text-white shadow-lg"
                    : "bg-white/5 text-[#94A3B8] hover:bg-white/10 hover:text-white"
                }`}
                style={activeType === type.id ? { 
                  backgroundColor: type.color,
                  boxShadow: `0 0 20px ${type.color}50`
                } : {}}
              >
                <type.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{type.name}</span>
                <span className="sm:hidden">{type.mobileShortName || type.name.split(" ")[0]}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="pb-8 relative overflow-visible">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* Golden Horse - Smaller and positioned to show pointer finger */}
          <div className="absolute left-[-100px] top-[-220px] w-[260px] h-[330px] pointer-events-none hidden lg:block" style={{ zIndex: 1 }}>
            <img 
              src="https://customer-assets.emergentagent.com/job_b2594af0-79d2-45da-b0e9-6c60003f7938/artifacts/00464ky5_ChatGPT%20Image%20Feb%205%2C%202026%2C%2010_49_29%20PM.png"
              alt="AI Horse Hero"
              className="w-full h-full object-contain"
            />
          </div>

          {/* Search Header with Post Button */}
          <div className="relative z-10">
            <MarketplaceSearchHeader
            listingType={activeType}
            config={activeConfig || listingTypes[0]}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            filters={filters}
            onOpenFilters={() => setFiltersOpen(true)}
            onOpenPostModal={() => openPostingModal(activeType)}
            activeFilterCount={countActiveFilters()}
            heroImage={heroImages[activeType]}
          />
          </div>

          <div className="flex gap-6">
            {/* Desktop Filters Sidebar */}
            {hasAdvancedFilters && !isMobile && (
              <MarketplaceFilters
                listingType={activeType}
                filters={filters}
                onFiltersChange={setFilters}
                facets={facets}
                onApply={handleApplyFilters}
                onClear={handleClearFilters}
                isMobile={false}
              />
            )}

            {/* Results Area */}
            <div className="flex-1">
              {/* Results Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="text-[#94A3B8] text-sm">
                  {isLoading ? (
                    "Loading..."
                  ) : (
                    <>
                      <span className="text-white font-medium">{totalResults.toLocaleString()}</span> results
                      {filters.state && <span> in {filters.state}</span>}
                    </>
                  )}
                </div>
                
                {/* Sort Dropdown + Save Search */}
                <div className="flex items-center gap-2">
                  <SaveSearchButton
                    filters={{ listing_type: activeType, q: searchQuery || undefined, ...filters }}
                    variant="ghost"
                    testIdPrefix="marketplace-save-search"
                  />
                  <select
                    value={sortBy}
                    onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
                    className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm [&>option]:bg-[#0F172A]"
                  >
                    {SORT_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Listings Grid */}
              {isLoading ? (
                <div className="flex justify-center py-20">
                  <Loader2 className="w-8 h-8 text-[#7C3AED] animate-spin" />
                </div>
              ) : listings.length > 0 ? (
                <>
                  <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
                    {listings.map((listing, idx) => (
                      hasAdvancedFilters ? (
                        <ListingCard
                          key={listing.id || idx}
                          listing={listing}
                          listingType={activeType}
                          onCompare={handleCompare}
                          isSelected={compareItems.some(i => i.id === listing.id)}
                          index={idx}
                        />
                      ) : (
                        // Legacy card for jobs/deals/events
                        <motion.div
                          key={listing.id || idx}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.05 }}
                          className="glass-card rounded-xl overflow-hidden hover:border-[#7C3AED]/50 transition-all group"
                        >
                          {listing.thumbnail_url && (
                            <div className="aspect-video relative overflow-hidden">
                              <img
                                src={listing.thumbnail_url}
                                alt={listing.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              />
                              <Badge 
                                className="absolute top-3 left-3 capitalize"
                                style={{ backgroundColor: `${activeConfig?.color || "#7C3AED"}90` }}
                              >
                                {listing.content_type?.replace("_", " ") || "Listing"}
                              </Badge>
                            </div>
                          )}
                          <div className="p-5">
                            <h3 className="text-white font-bold text-lg mb-2 line-clamp-2">{listing.title}</h3>
                            <p className="text-[#94A3B8] text-sm mb-4 line-clamp-2">{listing.description}</p>
                            
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 text-[#64748B] text-xs">
                                <Clock className="w-3 h-3" />
                                {listing.created_at ? new Date(listing.created_at).toLocaleDateString() : "Recently"}
                              </div>
                              <Link to={`/listing/${listing.id}`} onClick={() => window.scrollTo(0, 0)}>
                                <Button className="bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] hover:opacity-90">
                                  View <ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                              </Link>
                            </div>
                          </div>
                        </motion.div>
                      )
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center gap-2 mt-8">
                      <Button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        variant="outline"
                        className="border-white/10 text-white disabled:opacity-30"
                      >
                        Previous
                      </Button>
                      
                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (page <= 3) {
                            pageNum = i + 1;
                          } else if (page >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = page - 2 + i;
                          }
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setPage(pageNum)}
                              className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                                page === pageNum
                                  ? "bg-[#7C3AED] text-white"
                                  : "bg-white/5 text-[#94A3B8] hover:bg-white/10"
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      
                      <Button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        variant="outline"
                        className="border-white/10 text-white disabled:opacity-30"
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-20"
                >
                  {activeConfig && heroImages[activeType] && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="mb-6"
                    >
                      <div 
                        className="w-28 h-28 rounded-2xl overflow-hidden border-2 shadow-lg mx-auto"
                        style={{ borderColor: activeConfig?.color || "#7C3AED", boxShadow: `0 0 30px ${activeConfig?.color || "#7C3AED"}40` }}
                      >
                        <img
                          src={heroImages[activeType] || heroImages.jobs}
                          alt={activeConfig?.heroName || "AI Hero"}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </motion.div>
                  )}
                  <h3 className="text-white text-2xl font-bold mb-4">No Results Found</h3>
                  <p className="text-[#94A3B8] mb-6 max-w-md mx-auto">
                    Try adjusting your filters or search query to find more listings.
                  </p>
                  <Button onClick={handleClearFilters} variant="outline" className="border-white/10 text-white">
                    Clear Filters
                  </Button>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Filters Drawer */}
      {hasAdvancedFilters && (
        <MarketplaceFilters
          listingType={activeType}
          filters={filters}
          onFiltersChange={setFilters}
          facets={facets}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          isMobile={true}
          isOpen={filtersOpen}
          onClose={() => setFiltersOpen(false)}
        />
      )}

      {/* Compare Bar */}
      {hasAdvancedFilters && (
        <CompareBar
          items={compareItems}
          listingType={activeType}
          onRemove={(id) => setCompareItems(compareItems.filter(i => i.id !== id))}
          onClear={() => setCompareItems([])}
        />
      )}

      {/* Posting Chat Modal */}
      <PostingChatModal
        listingType={postingType}
        isOpen={postingModalOpen}
        onClose={() => setPostingModalOpen(false)}
      />

      <BackgroundVideoStrip src={BG_VIDEOS.marketplace} testid="marketplace-bg-video" />
    </div>
  );
}
